/**
 * O confronto em destaque: dois clubes frente a frente, com a campanha de cada
 * um em contadores rolantes e o placar no meio.
 *
 * É a seção do template que mais depende de dado real, e a que ficaria mais
 * constrangedora com dado inventado: um placar fixo de "2 x 3" entre dois times
 * que nunca se enfrentaram é a primeira coisa que um capitão nota.
 *
 * O que aparece aqui sai inteiro da edição em destaque:
 *
 *   os escudos      os dois times da partida
 *   os contadores   pontos, vitórias, derrotas e empates, da classificação
 *   o placar        o resultado, quando já houve; a data, quando ainda não
 *
 * Sem partida para mostrar — edição sem sorteio, ou nenhuma edição — a seção
 * não renderiza. Um placar zerado entre dois espaços vazios não informa nada e
 * ainda ocupa uma tela inteira de rolagem.
 */

import { ehEscudoPadrao } from '@mpc/core';
import { useRef } from 'react';
import { Link } from 'react-router';
import { Contador } from './Contador';
import { useEntradaDaSecao } from './animacoes';
import { useEdicaoEmDestaque, partidaEmDestaque } from './useEdicaoEmDestaque';
import { TeamCrest } from '@/components/ui';
import type { MatchDto, StandingRowDto, TeamDto } from '@/lib/api';

export function SecaoTransmissao() {
  const raiz = useRef<HTMLElement>(null);
  const { edicao, partidas, times, classificacao, carregando } = useEdicaoEmDestaque();

  /* A mais decisiva entre as que estão para acontecer — ver o comentário em
     `partidaEmDestaque`. Sem nenhuma pendente, a última que foi decidida. */
  const partida = partidaEmDestaque(partidas);
  const casa = partida?.homeTeamId ? times[partida.homeTeamId] : undefined;
  const fora = partida?.awayTeamId ? times[partida.awayTeamId] : undefined;

  useEntradaDaSecao(raiz, '[data-entra]', { dependencias: [partida?.id ?? ''] });

  if (carregando || !partida || !casa || !fora || !edicao) return null;

  const jogada = partida.homeGoals !== null && partida.awayGoals !== null;

  return (
    <section className="streaming-section" ref={raiz}>
      {/*
        O template escreve só o nome da edição aqui, em corpo pequeno, e quem
        chega na página não tem como saber o que são os dois cartões. A etiqueta
        diz o que o bloco é — e ela é a mesma pílula da hero (`.momentum-block`).
      */}
      <div className="streaming-subtitle">
        <span className="momentum-block">
          {jogada ? 'Resultado' : 'Próximo confronto'} · {edicao.name}
        </span>
      </div>

      <div className="container large-container">
        <div className="streaming-wrapper">
          <CartaoDoClube time={casa} linha={classificacao[casa.id]} />

          <div className="streaming-middle-content">
            <div className="streaming-timing" data-entra>
              <h3 className="streaming-date">{dataPorExtenso(partida.scheduledAt)}</h3>
              <p className="large-paragraph">{hora(partida.scheduledAt)}</p>
            </div>

            {/*
              Antes do jogo o quadro mostra o confronto, não um 0 a 0. Zero a
              zero é um resultado — anunciá-lo antes da bola rolar seria
              publicar um placar que ninguém jogou.

              O que ficava no lugar do placar era um "·", e a Anton não tem esse
              caractere: o navegador desenhava a caixinha de glifo ausente, dois
              quadrados brancos no meio da seção. "VS" é a mesma palavra que a
              lista de partidas e o chaveamento usam para um confronto que ainda
              vai acontecer.
            */}
            {jogada ? (
              <div className="score-board" data-entra>
                <div className="score">{partida.homeGoals}</div>
                <div className="score-line" aria-hidden />
                <div className="score">{partida.awayGoals}</div>
              </div>
            ) : (
              <div className="score-board" data-entra>
                <div className="score score-vs">VS</div>
              </div>
            )}

            <div className="streaming-buttons" data-entra>
              <Link to={`/partidas/${partida.id}`} className="red-button">
                <div className="red-button-text">
                  {jogada ? 'Ver a partida' : 'Detalhes do confronto'}
                </div>
                <div className="red-button-bg" aria-hidden />
              </Link>
            </div>
          </div>

          <CartaoDoClube time={fora} linha={classificacao[fora.id]} />
        </div>
      </div>
    </section>
  );
}

/**
 * Um lado do confronto.
 *
 * Os quatro números são os da campanha do time na edição. Quando a
 * classificação ainda não existe — edição sorteada mas sem rodada jogada — eles
 * são zeros de verdade, e não ausência de dado: o time realmente não somou nada
 * ainda.
 */
function CartaoDoClube({ time, linha }: { time: TeamDto; linha: StandingRowDto | undefined }) {
  const campanha = [
    { rotulo: 'Pontos', valor: linha?.points ?? 0 },
    { rotulo: 'Vitórias', valor: linha?.wins ?? 0 },
    { rotulo: 'Derrotas', valor: linha?.losses ?? 0 },
    { rotulo: 'Empates', valor: linha?.draws ?? 0 },
  ];

  return (
    <div className="streaming-card" data-entra>
      {time.crestUrl && !ehEscudoPadrao(time.crestUrl) ? (
        <img src={time.crestUrl} alt="" aria-hidden loading="lazy" className="club-logo" />
      ) : (
        <div className="club-logo">
          <TeamCrest team={time} size="xl" />
        </div>
      )}

      <div className="club-name">{time.name}</div>

      <div className="streaming-counter-block">
        {campanha.map((item) => (
          <div key={item.rotulo} className="streaming-counter-wrap">
            <div className="point-text">{item.rotulo}</div>
            <Contador valor={item.valor} variante="transmissao" />
          </div>
        ))}
      </div>
    </div>
  );
}

function dataPorExtenso(epochSegundos: number | null): string {
  if (!epochSegundos) return 'Data a definir';
  return new Date(epochSegundos * 1000).toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  });
}

function hora(epochSegundos: number | null): string {
  if (!epochSegundos) return 'Horário a definir';
  const formatada = new Date(epochSegundos * 1000).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${formatada} (horário de Brasília)`;
}
